import { memoize } from 'util/memoization'
import { createSelector } from 'reselect'

import { selectAgentsById } from 'selectors/agents/base'
import { selectGroupsById, selectPreviousMailboxId } from 'selectors/app'
import {
  selectCurrentUser,
  selectPreferredMailboxId,
} from 'ducks/currentUser/selectors'
import { selectAttachmentsByTicketId } from 'selectors/attachments/base'
import { selectCustomersById } from 'selectors/customers'
import { selectLabelsById } from 'selectors/labels/base'
import {
  selectFirstMailboxId,
  selectMailboxIdFromUrl,
} from 'selectors/mailboxes_base'

import { getActorCurried } from 'util/actors'
import {
  ticketHasAttachments,
  getFirstOrEmptyFromDraftRecipient,
} from 'util/draft'
import { isEmpty as isEmptyObject } from 'util/objects'
import { isHtmlEmpty } from 'util/strings'

import { selectCurrentTicket } from 'selectors/tickets/current'
import { selectDefaultNote } from './replyPrefs'
import { selectFrozenDraft } from './frozenDraft'
import { createNoteDraftBuilder, getPlainBodyOrFrozen } from '../util'

const selectUndecoratedTicketNoteDrafts = state => state.drafts.notes || {}

const selectNoteDraftBuilder = createSelector(
  selectUndecoratedTicketNoteDrafts,
  selectAgentsById,
  selectGroupsById,
  selectCurrentUser,
  selectDefaultNote,
  selectFirstMailboxId,
  selectPreviousMailboxId,
  selectPreferredMailboxId,
  selectCustomersById,
  selectMailboxIdFromUrl,
  selectLabelsById,
  (
    noteDrafts,
    agentsById,
    groupsById,
    currentUser,
    defaultNote,
    firstMailboxId,
    previousMailboxId,
    preferredMailboxId,
    customersById,
    mailboxIdFromUrl,
    labelsById
  ) => {
    return ticket => {
      return createNoteDraftBuilder(
        noteDrafts,
        ticket,
        agentsById,
        groupsById,
        currentUser,
        defaultNote,
        firstMailboxId,
        previousMailboxId,
        preferredMailboxId,
        customersById,
        mailboxIdFromUrl,
        labelsById
      )
    }
  }
)

export const selectNoteDraft = createSelector(
  selectCurrentTicket,
  selectNoteDraftBuilder,
  (ticket, builder) => builder(ticket)
)

const toRecipientsExtractor = memoize((customersById, draft) => {
  if (!draft || !draft.to) return []

  const getCustomer = getActorCurried({}, customersById)
  const { to } = draft
  const first = Array.isArray(to) ? to[0] : to

  return [getCustomer(first)]
})

export const selectNoteDraftRecipients = createSelector(
  selectNoteDraft,
  selectCustomersById,
  (draft, customersById) => {
    return toRecipientsExtractor(customersById, draft)
  }
)

const selectIsAttachingToNoteDraft = createSelector(
  selectAttachmentsByTicketId,
  selectNoteDraft,
  (byId, draft) => byId && byId[draft.id] && !isEmptyObject(byId[draft.id])
)

export const selectCommentIsSendable = createSelector(
  selectNoteDraft,
  selectIsAttachingToNoteDraft,
  (draft, isAttaching) => {
    if (!draft) return false
    if (isAttaching) return false
    if (!isHtmlEmpty(draft.body)) return true
    if (ticketHasAttachments(draft)) return true
    return false
  }
)

export const selectNewCommentIsSendable = createSelector(
  selectNoteDraft,
  selectIsAttachingToNoteDraft,
  (draft, isAttaching) => {
    const to = getFirstOrEmptyFromDraftRecipient('to', draft)
    if (!draft) return false
    if (isEmptyObject(to)) return false
    if (isAttaching) return false
    if (!isHtmlEmpty(draft.body)) return true
    if (ticketHasAttachments(draft)) return true
    return false
  }
)

export const selectNewNoteDraftMailboxId = createSelector(
  selectUndecoratedTicketNoteDrafts,
  drafts => {
    const newDraft = drafts.new
    if (!newDraft) return null

    return newDraft.mailboxId
  }
)

export const makeSelectTicketNoteDraftBodyPlainText = ticketId => {
  return createSelector(
    selectUndecoratedTicketNoteDrafts,
    selectFrozenDraft,
    getPlainBodyOrFrozen(ticketId, false)
  )
}
